import React, { useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fileToBase64, urlToFile } from '../../utils/fileHandling';
import { faArrowLeftLong, faImage} from '@fortawesome/free-solid-svg-icons';
import { useAiImageTypesGeneration } from '../../hooks/useAiImageTypesGeneration';
import { aiStateTypes} from '../../types/ai/states';
import { downloadOriginal } from '../../utils/exportMedia';
import { ApiPayment } from '../../apis/ApiPayment';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ImageGenerationBodyProps, samplerList } from '../../types/ai/api';
import { ImageGernerationSimpleInputProps } from '../../types/imageSimple/imageGeneration';
import { useUpdateImageGenerationCosts } from '../../hooks/useUpdateImageGenerationCosts';
import { handleGetRandomNumber } from '../../utils/calculations';
import { getImagePixelSize } from '../../utils/images/imageConvertFormats';
import { styleDescriptionPrompt } from '../../utils/aiModels/prompts';
import { parsePromptResponse } from '../../utils/chat/chatPropsParsing';
import { StatePreview } from '../../components/app/generation/StatePreview';
import { FilePlaceHolderWithDragAndDrop } from '../../components/fileupload/filePlaceholderWithDragAndDrop';
import { FilePlaceHolderVideo } from '../../components/fileupload/filePlaceholderVideo';
import { PromptArea } from '../../components/app/generation/PromptArea';
import { SettingAIOptions } from '../../components/app/generation/SettingAIOptions';
import { GenerationFormatType, usePromptHook } from '../../hooks/usePromptHook';
import { useNavigateBack } from '../../hooks/useNavigationBack';

export const ImageToVideoGeneration = () => {
    const { t } = useTranslation(['generation','toolsApps']);
    const isMobile = useIsMobile();
    const navigateBack = useNavigateBack();
    const [image,setImage] = useState<{file:any,src:string}|null>(null);
    const [generateVideo, setGenerateVideo] = useState(false);
    const [inputsImgGeneration,setInputsImgGeneration] = useState<ImageGernerationSimpleInputProps>({
        promptNegative:'',
        promptPositive: '',
        samplingSteps: 12,
        cfg: 2.5,
        sampler: samplerList.length > 0 ? samplerList[0] : 'Euler',
        seed: 42,
        scheduler: 'simple',
        style: 'none',
        quality: 'Standard',
        aspectRatio: '1:1 (quadratic)',
        numberOgGeneration: 1,
        reference: null
    });
    const [seedRandomActive, setSeedRandomActive] = useState(true);
    const [inputsVideoGeneration, setInputsVideoGeneration] = useState('');
    const [creationCosts, updateCosts] = useUpdateImageGenerationCosts();
    const [video, setVideo] = useState<string|null>(null);
    const [generationIMG,stateIMG,setStateIMG,getJobIDIMG,insufficientCreditsDialogIMG] = useAiImageTypesGeneration();
    const [generationVideo,stateVideo,setStateVideo,getJobIDVideo,insufficientCreditsDialogVideo] = useAiImageTypesGeneration();
    const [generationLLM,stateLLM,setStateLLM,handleRandomPromptExternal,handleCheckPromptExternal, _,insufficientCreditsDialogLLM] = usePromptHook();
    const [aiState, setAiState] = useState<aiStateTypes>(null);
    const [aiGenerationIsRunning, setAiGenerationIsRunning] = useState(false);
    const [videoCosts, setVideoCosts] = useState<number|null>(0);

    // video_gen is a fixed price (no params yet), so estimate once on mount. The image step (step 1)
    // has no cost preview: this app's image_gen call sends no dimensions (backend defaults apply),
    // so there's no width/height to estimate against - see TASKS.md Kosten-Audit (2026-07-21).
    const updateVideoCosts = async() => {
        try {
            const response = await ApiPayment.GetCostsForVideoCreate();
            setVideoCosts(response.costs);
        } catch {
            console.log('Error in calculation video creation costs.');
        }
    };
    const handleRandomPrompt = async (format: GenerationFormatType) => {
        setStateIMG(null);
        setStateVideo(null);
        setAiGenerationIsRunning(true);
        if(format === 'video'){
            setGenerateVideo(true);
        }
        await handleRandomPromptExternal('create',true,format); 
    };
    const handleCheckPrompt = async (format: GenerationFormatType) => {
        setStateIMG(null);
        setStateVideo(null);
        setAiGenerationIsRunning(true);
        if(format === 'video'){
            setGenerateVideo(true);
        }
        await handleCheckPromptExternal(inputsImgGeneration.promptPositive,inputsImgGeneration.promptNegative, 'create',format);
    };

    const handleGenerationResultImage = async() => {
        setAiGenerationIsRunning(false);
        let img = generationIMG[0];
        let imgFile = await urlToFile(img,img.replaceAll('\\','/').split('/').slice(-1)[0]);
        setImage({
            file:imgFile,
            src:img
        });
        if(seedRandomActive){
            const currentInputs = {...inputsImgGeneration};
            currentInputs.seed = handleGetRandomNumber();
            setInputsImgGeneration(currentInputs);
        }
    };
    const handleGenerationResultVideo = () => {
        setAiGenerationIsRunning(false);
        let video = generationVideo[0];
        setVideo(video);
    };

    const generateImage = async() => {
        setStateIMG(null);
        setStateVideo(null);
        setStateLLM(null);
        setAiGenerationIsRunning(true);
        //request to get job id
        const imageDimension = getImagePixelSize(inputsImgGeneration.aspectRatio, inputsImgGeneration.quality ?? 'standard');
        const prompt = styleDescriptionPrompt(inputsImgGeneration.promptPositive,inputsImgGeneration.promptNegative, inputsImgGeneration.style, false);
        const body: ImageGenerationBodyProps = {
            positive_prompt: prompt.positive_prompt, 
            negative_prompt: prompt.negative_prompt ? prompt.negative_prompt : undefined,
            width: imageDimension.width,
            height: imageDimension.height,
            seed: inputsImgGeneration.seed,
            steps: inputsImgGeneration.samplingSteps,
            scheduler: inputsImgGeneration.scheduler,
            model: 'Qwen_Image',
            cfg: inputsImgGeneration.cfg,
            number_of_images: 1,
            sampler: inputsImgGeneration.sampler
        };
        setStateIMG('send');
        let path = 'generate/image'
        try {
            getJobIDIMG('image_gen',path,body);
        } catch(err){
            setStateIMG('error');
            setAiGenerationIsRunning(false);
        };
    };
    const handleGenerateVideoFromImage = async () => {
        setGenerateVideo(true);
        setStateVideo(null);
        setStateIMG(null);
        setStateLLM(null);
        setAiGenerationIsRunning(true);
        const prompt = inputsVideoGeneration;
        if(!image){
            setStateVideo('error');
            setAiGenerationIsRunning(false);
            return;
        }
        const file = image.file;
        const base64Image = await fileToBase64(file);
        const body = {
            "positive_prompt": prompt,
            "image": base64Image
        };
        try {
            setStateVideo('send');
            let path = 'generate/image-to-video'
            getJobIDVideo('video_gen',path,body);
        } catch(err){
            setStateVideo('error');
            setAiGenerationIsRunning(false);
        }
    };
    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputsImgGeneration(prev => ({...prev,[name]: value}));
    };
    const uploadFile = async(event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0]  : null;
        if(file){
            const imgSrc = URL.createObjectURL(file);
            setImage({
                'file':file,
                'src':imgSrc
            });
        }
    };
    const download = (imageDownload:boolean) => {
        let resource = '';
        if (imageDownload && image){
            resource = image.src;
        }
        else if (video) {
            resource = video;
        }
        if(resource === '') return;
        const fileName = resource.replaceAll('\\','/').split('/').slice(-1)[0];
        downloadOriginal(resource, fileName)
            .catch(err => console.error('Download fehlgeschlagen', err));
    };
    const handleDroppedImage = (file: File) => {
        if (!file.type.startsWith("image/")) return;

        const src = URL.createObjectURL(file);
        setImage({ file, src });
    };

    useEffect(()=>{
        document.title = t('apps:registry.app.imageToVideo.name')
    },[]);
    useEffect(()=>{
        if(generationIMG.length > 0){
            handleGenerationResultImage();
        }
    },[generationIMG]);
    useEffect(()=>{
        if(generationVideo.length > 0){
            handleGenerationResultVideo();
        }
    },[generationVideo]);
    useEffect(()=>{
        setGenerateVideo(false);
    },[image]);
    useEffect(() => {
        setAiGenerationIsRunning(false);
        setStateLLM(null);
        if (!generationLLM) return;
        const promptResponse = parsePromptResponse(generationLLM);
        if (!promptResponse) return;

        if(generateVideo){
            setGenerateVideo(false);
            setInputsVideoGeneration(generationLLM.positive_prompt);
        }
        else{
            setInputsImgGeneration(prev => ({
                ...prev,
                prompt_positive: promptResponse.positive_prompt,
                prompt_negative: promptResponse.negative_prompt ?? ""
            }));
        }
    }, [generationLLM]);
    useEffect(()=>{
        updateVideoCosts();
    },[]);
    useEffect(()=>{
        updateCosts(inputsImgGeneration);
    },[inputsImgGeneration]);
    useEffect(()=>{
        if(stateIMG === 'done' || stateIMG === 'error' || stateIMG === 'unavailable' || stateLLM === 'error' || stateLLM === 'unavailable' || stateLLM === 'done' || stateVideo === 'error' || stateVideo === 'unavailable' || stateVideo === 'done'){
            setAiGenerationIsRunning(false);
        }
        if(stateIMG !== null){
            setStateLLM(null);
            setStateVideo(null);
            setAiState(stateIMG);
        }
        if(stateLLM !== null){
            setStateIMG(null);
            setStateVideo(null);
            setAiState(stateLLM);
        }
        if(stateVideo !== null){
            setStateIMG(null);
            setStateLLM(null);
        }
        if(!stateIMG && !stateLLM){
            setAiState(null);
        }
    },[stateIMG, stateVideo,stateLLM]);

  return (
    <>
        {insufficientCreditsDialogIMG}
        {insufficientCreditsDialogVideo}
        {insufficientCreditsDialogLLM}
        <div className="container-fluid h-100 pb-2">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <button 
                            onClick={() => navigateBack()}
                            className="btn btn-outline-secondary btn-sm ms-3"
                            title={t('toolsApps:back')}
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </button>
                    </div>
                    <div className='col'/>
                    <div className='col-auto h3 align-items-center'>
                        {t('generateVideo')}
                    </div>
                    <div className='col'/>
                </div>
                <div className={`row flex-grow-1 p-2 m-0 ${isMobile ? "overflow-y-auto overflow-x-hidden" : ""}`} style={{ minHeight: 0 }}>
                    <div className={`${!isMobile ? "col-6 h-100" : "col-12"} p-2 d-flex flex-column`}>
                        <div className='row'>
                            <div className='col h4'>
                                {t('step')} 1
                            </div>
                            <div className='col-auto h4'>
                                {t('generateImage')}
                            </div>
                            <div className='col'/>
                        </div>
                        <StatePreview state={aiState} />
                        <div className={`position-relative w-100 d-flex align-items-center justify-content-center overflow-hidden ${!isMobile ? "flex-grow-1" : ""}`}
                            style={{ minHeight: image ? (isMobile ? "250px" : "350px") : "160px", maxHeight: isMobile ? "50vh" : "100%"}}
                        >
                            <FilePlaceHolderWithDragAndDrop 
                                imageSrc={ image ? image.src : null}
                                imageAltText={`${t('lastGeneratedImage')}: ${image?.file.name}`}
                                onDownload={() => download(true)}
                                donwloadTitle={t("download.image")}
                                onDropImage={handleDroppedImage}
                                placeholderIcon={faImage}
                                placeholderText={t('noGeneratedImage')}
                            />
                        </div>
                        <div className="flex-shrink-0 mt-1">
                            <PromptArea
                                functionsDisabled={aiGenerationIsRunning}
                                credits={creationCosts ?? 0}
                                onCheckPrompt={() => handleCheckPrompt('image')}
                                onRandomPrompt={() => handleRandomPrompt('image')}
                                onGenerateImage={generateImage}
                                promptNegative={inputsImgGeneration.promptNegative ?? ''}
                                promptPositive={inputsImgGeneration.promptPositive ?? ''}
                                onInputChange={handleInputChange}
                                onUploadFile={uploadFile}
                            />
                            {   isMobile && (
                                    <SettingAIOptions
                                        aspectRatio={inputsImgGeneration.aspectRatio}
                                        style={inputsImgGeneration.style}
                                        sampler={inputsImgGeneration.sampler}
                                        samplingSteps={inputsImgGeneration.samplingSteps}
                                        seed={inputsImgGeneration.seed}
                                        seedRandomActive={seedRandomActive}
                                        setSeedRandomActive={setSeedRandomActive}
                                        cfg={inputsImgGeneration.cfg}
                                        scheduler={inputsImgGeneration.scheduler}
                                        quality={inputsImgGeneration.quality}
                                        onInputsChange={setInputsImgGeneration}
                                    />
                                )
                            }
                        </div>
                    </div>
                    <div className={`${!isMobile ? "col-6 h-100" : "col-12"} p-2 d-flex flex-column`}>
                        <div className='row'>
                            <div className='col h4'>
                                {t('step')} 2
                            </div>
                            <div className='col-auto h4'>
                                {t('generateVideo')}
                            </div>
                            <div className='col'/>
                        </div>
                        <StatePreview state={stateVideo} />
                        <div className={`position-relative w-100 d-flex align-items-center justify-content-center overflow-hidden ${!isMobile ? "flex-grow-1" : ""}`}
                            style={{ minHeight: video ? (isMobile ? "250px" : "350px") : "160px", maxHeight: isMobile ? "50vh" : "100%"}}
                        >
                            <FilePlaceHolderVideo
                                url={ video ?? null}
                                onDownload={() => download(false)}
                                donwloadTitle={t('download.video')}
                            />
                        </div>
                        <div className="flex-shrink-0 mt-1">
                            <PromptArea
                                functionsDisabled={aiGenerationIsRunning}
                                credits={videoCosts ?? 0}
                                onGenerateImage={handleGenerateVideoFromImage}
                                promptPositive={inputsVideoGeneration}
                                onInputChange={(e) => setInputsVideoGeneration(e.target.value)}
                                placeholderPromptPositive={t('placeholder.animate')}
                                onCheckPrompt={() => handleCheckPrompt('video')}
                                onRandomPrompt={() => handleRandomPrompt('video')}
                            />
                        </div>
                    </div>
                </div>
                {   !isMobile && (
                        <div className='row mx-0'>
                            <div className='col-6'>
                                <SettingAIOptions
                                    aspectRatio={inputsImgGeneration.aspectRatio}
                                    style={inputsImgGeneration.style}
                                    sampler={inputsImgGeneration.sampler}
                                    samplingSteps={inputsImgGeneration.samplingSteps}
                                    seed={inputsImgGeneration.seed}
                                    seedRandomActive={seedRandomActive}
                                    setSeedRandomActive={setSeedRandomActive}
                                    cfg={inputsImgGeneration.cfg}
                                    scheduler={inputsImgGeneration.scheduler}
                                    quality={inputsImgGeneration.quality}
                                    onInputsChange={setInputsImgGeneration}
                                />
                            </div>
                        </div>
                    )
                }
            </div>
        </div>
    </>
  );
};

export default ImageToVideoGeneration;